// The building blocks screens are made of, on Preact (written with `h`, no JSX build step). Semantic HTML first:
// real buttons, labelled inputs, headings, role=switch / radio / tab where HTML has no element; every control has an
// accessible name (decision 0063: "menus and screens as semantic HTML with ARIA labels"). Text comes in already
// translated: the game's strings table is the only source (the package never holds player-facing text).
import { h, type ComponentChildren, type VNode } from "preact";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { UI_FAULTS } from "./faults";

/** The icons, drawn in SVG (UI art in code). */
export type IconName = "back" | "close" | "rotate" | "reset" | "warn" | "plus" | "minus";

const ICON_PATHS: Record<IconName, string> = {
  back: "M15 5 8 12l7 7",
  close: "M6 6l12 12M18 6 6 18",
  rotate: "M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM11 18h2M3 14a9 9 0 0 0 5 6M21 10a9 9 0 0 0-5-6",
  reset: "M4 12a8 8 0 1 0 2.4-5.7M4 4v5h5",
  warn: "M12 4 2.5 20h19zM12 10v4M12 17v.5",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
};

/** An icon (decorative: hidden from assistive tech; the control around it carries the name). */
export function Icon({ name }: { name: IconName }): El {
  return h(
    "svg",
    { viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", fill: "none", stroke: "currentColor", "stroke-width": 2.2, "stroke-linecap": "round", "stroke-linejoin": "round" },
    h("path", { d: ICON_PATHS[name] }),
  );
}

/** A text button. */
export function Button(p: {
  label: string;
  onPress: () => void;
  autofocus?: boolean;
  variant?: "primary" | "plain";
  disabled?: boolean;
  /** Extra description read after the name (an element id). */
  describedBy?: string;
  /** For tests and analytics: `data-action`. */
  action?: string;
}): El {
  return h(
    "button",
    {
      type: "button",
      class: "rpg-btn",
      "data-variant": p.variant ?? "plain",
      "data-autofocus": p.autofocus ? "" : undefined,
      "data-action": p.action,
      disabled: p.disabled,
      "aria-describedby": p.describedBy,
      onClick: () => p.onPress(),
    },
    p.label,
  );
}

/** An icon-only button; `label` is its accessible name (planted defect UI_FAULTS.unlabelledButton drops it). */
export function IconButton(p: { icon: IconName; label: string; onPress: () => void; action?: string }): El {
  return h(
    "button",
    {
      type: "button",
      class: "rpg-btn rpg-icon-btn",
      "aria-label": UI_FAULTS.unlabelledButton ? undefined : p.label,
      title: UI_FAULTS.unlabelledButton ? undefined : p.label,
      "data-action": p.action,
      onClick: () => p.onPress(),
    },
    h(Icon, { name: p.icon }),
  );
}

/** A labelled slider with its value spoken and shown. */
export function Slider(p: { id: string; label: string; value: number; min: number; max: number; step?: number; valueText: string; onChange: (v: number) => void }): El {
  const set = (e: Event): void => p.onChange(Number((e.currentTarget as HTMLInputElement).value));
  return h(
    "div",
    { class: "rpg-row", "data-setting": p.id },
    h("label", { for: p.id }, p.label),
    h(
      "div",
      { class: "rpg-slider" },
      h("input", { id: p.id, type: "range", min: p.min, max: p.max, step: p.step ?? "any", value: p.value, "aria-valuetext": p.valueText, onInput: set }),
      h("output", { for: p.id, "aria-hidden": "true" }, p.valueText),
    ),
  );
}

/** An on/off switch. */
export function Toggle(p: { id: string; label: string; on: boolean; onText: string; offText: string; onChange: (on: boolean) => void }): El {
  const labelId = `${p.id}-label`;
  return h(
    "div",
    { class: "rpg-row", "data-setting": p.id },
    h("span", { class: "rpg-label", id: labelId }, p.label),
    h("button", { type: "button", id: p.id, class: "rpg-btn", role: "switch", "aria-checked": p.on ? "true" : "false", "aria-labelledby": labelId, onClick: () => p.onChange(!p.on) }, p.on ? p.onText : p.offText),
  );
}

/**
 * A labelled text field (email, password, a name). Uncontrolled: the browser keeps what is typed, `onInput` reports
 * it. A keyboard's left and right move the caret inside it; up and down still move between items.
 */
export function TextField(p: {
  id: string;
  label: string;
  type?: "text" | "email" | "password";
  value?: string;
  /** The browser's autofill hint ("email", "current-password"). */
  autocomplete?: string;
  autofocus?: boolean;
  required?: boolean;
  onInput: (value: string) => void;
}): El {
  return h(
    "div",
    { class: "rpg-row rpg-field" },
    h("label", { for: p.id }, p.label),
    h("input", {
      id: p.id,
      name: p.id,
      type: p.type ?? "text",
      value: p.value,
      autocomplete: p.autocomplete,
      required: p.required,
      spellcheck: false,
      "data-autofocus": p.autofocus ? "" : undefined,
      onInput: (e: Event) => p.onInput((e.currentTarget as HTMLInputElement).value),
    }),
  );
}

/** One of several options, as a radio group in one navigation row (left and right move and select). */
export function Choice(p: { id: string; label: string; value: string; options: readonly { value: string; label: string }[]; onChange: (v: string) => void }): El {
  const labelId = `${p.id}-label`;
  return h(
    "div",
    { class: "rpg-row", "data-setting": p.id },
    h("span", { class: "rpg-label", id: labelId }, p.label),
    h(
      "div",
      { class: "rpg-options", role: "radiogroup", "aria-labelledby": labelId, "data-nav-row": "" },
      p.options.map((o) =>
        h(
          "button",
          { type: "button", class: "rpg-btn", role: "radio", "aria-checked": o.value === p.value ? "true" : "false", "data-value": o.value, onClick: () => p.onChange(o.value) },
          o.label,
        ),
      ),
    ),
  );
}

/** A row of tabs (one navigation row) controlling a panel. */
export function Tabs(p: { id: string; label: string; tabs: readonly { id: string; label: string }[]; selected: string; onSelect: (id: string) => void }): El {
  return h(
    "div",
    { class: "rpg-tabs", role: "tablist", "aria-label": p.label, "data-nav-row": "" },
    p.tabs.map((t) =>
      h(
        "button",
        {
          type: "button",
          class: "rpg-btn",
          role: "tab",
          id: `${p.id}-tab-${t.id}`,
          "aria-selected": t.id === p.selected ? "true" : "false",
          "aria-controls": `${p.id}-panel`,
          "data-tab": t.id,
          onClick: () => p.onSelect(t.id),
        },
        t.label,
      ),
    ),
  );
}

/** A tab's panel. */
export function TabPanel(p: { id: string; selected: string; children?: ComponentChildren }): El {
  return h("div", { role: "tabpanel", id: `${p.id}-panel`, "aria-labelledby": `${p.id}-tab-${p.selected}` }, p.children);
}

/** A titled section of a screen. */
export function Group(p: { id: string; title: string; children?: ComponentChildren }): El {
  return h("section", { "aria-labelledby": `${p.id}-title`, "data-group": p.id }, h("h2", { id: `${p.id}-title` }, p.title), p.children);
}

/** A vertical menu of buttons. */
export function Menu(p: { label?: string; children?: ComponentChildren }): El {
  return h("div", { class: "rpg-menu", role: "group", "aria-label": p.label }, p.children);
}

/** A list of rows (an inventory tab, a shop's stock, a skill tree): a labelled `ul`. */
export function List(p: { id: string; label: string; children?: ComponentChildren }): El {
  return h("ul", { class: "rpg-list", "aria-label": p.label, "data-list": p.id }, p.children);
}

/**
 * One row of a list: a decorative icon (the game's, drawn in code), a title, an optional detail line, an optional badge
 * ("Equipped"), and its actions as one navigation row (left and right move between them).
 */
export function ListRow(p: { id: string; title: string; detail?: string; badge?: string; icon?: El | null; children?: ComponentChildren }): El {
  const tid = `rpg-row-${p.id}`;
  return h(
    "li",
    { class: "rpg-list-row", "data-row": p.id, "aria-labelledby": tid },
    p.icon ? h("span", { class: "rpg-list-icon", "aria-hidden": "true" }, p.icon) : null,
    h(
      "div",
      { class: "rpg-list-text" },
      h("span", { class: "rpg-list-title", id: tid }, p.title),
      p.badge ? h("span", { class: "rpg-list-badge" }, p.badge) : null,
      p.detail ? h("span", { class: "rpg-list-detail" }, p.detail) : null,
    ),
    p.children ? h("div", { class: "rpg-list-actions", "data-nav-row": "" }, p.children) : null,
  );
}

/** A labelled meter (experience toward the next level): a progressbar with its value spoken and shown. */
export function Meter(p: { id: string; label: string; value: number; max: number; valueText: string }): El {
  const max = p.max > 0 && Number.isFinite(p.max) ? p.max : 1;
  const v = Math.max(0, Math.min(max, p.value));
  return h(
    "div",
    { class: "rpg-meter", "data-meter": p.id },
    h("span", { class: "rpg-label", id: `${p.id}-label` }, p.label),
    h(
      "div",
      { class: "rpg-meter-bar", role: "progressbar", "aria-labelledby": `${p.id}-label`, "aria-valuemin": 0, "aria-valuemax": max, "aria-valuenow": v, "aria-valuetext": p.valueText },
      h("div", { class: "rpg-meter-fill", style: `width: ${((v / max) * 100).toFixed(1)}%` }),
    ),
    h("span", { class: "rpg-meter-text", "aria-hidden": "true" }, p.valueText),
  );
}

/** A live status line (announced politely). */
export function Status(p: { id?: string; text: string }): El {
  return h("p", { class: "rpg-status", id: p.id, role: "status" }, p.text);
}
